import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

/**
 * DetailPanel の内側パディング（p:3 = 24px）を打ち消して、
 * ヘッダーをパネル上端に貼り付ける（sticky）ための sx。
 * 各フォームで重複していた STICKY_HEADER_SX を共通化したもの。
 */
export const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

/** DetailPanel の p:3 (24px) を打ち消す sticky フッター sx（保存/更新ボタン等） */
export const STICKY_FOOTER_SX = {
  position: 'sticky',
  bottom: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderTop: 1,
  borderColor: 'divider',
  py: 2,
  mx: -3,
  px: 3,
} as const;

type Props = {
  /** 見出し */
  title: string;
  /** 補足説明（見出し下の説明文） */
  subtitle?: string;
  /** 戻る押下時のハンドラ（指定時のみ戻るアイコンを表示） */
  onBack?: () => void;
  /** タイトル右に表示するステータスチップ等（任意） */
  status?: React.ReactNode;
  /** コピー可能なID（指定時のみコピーアイコン付きで右上に表示） */
  copyId?: string;
  /** 右下に表示するメタ情報（作成者・作成日など・任意） */
  meta?: React.ReactNode;
  /** タイトル行の右端に表示するアクション（任意） */
  actions?: React.ReactNode;
};

/**
 * 詳細/フォームパネル共通の sticky ヘッダー。
 * 各ドメインのフォーム（Create/Update）で重複していたヘッダー構造を共通化する。
 * - 左: 戻るアイコン + タイトル + ステータスチップ
 * - 右: コピー可能なID + アクション
 * - 下段: サブタイトル + メタ情報
 */
const DetailFormHeader: React.FC<Props> = ({
  title,
  subtitle,
  onBack,
  status,
  copyId,
  meta,
  actions,
}) => {
  const [copyTooltip, setCopyTooltip] = React.useState('Copy to clipboard');

  const handleCopy = () => {
    if (!copyId) return;
    navigator.clipboard.writeText(copyId).then(() => {
      setCopyTooltip('Copied!');
      setTimeout(() => setCopyTooltip('Copy to clipboard'), 2000);
    });
  };

  return (
    <Box sx={STICKY_HEADER_SX}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight={700} noWrap>
            {title}
          </Typography>
          {status}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {actions}
          {copyId && (
            <>
              <Typography variant="caption" color="text.secondary">
                {copyId}
              </Typography>
              <Tooltip title={copyTooltip}>
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>
      {(subtitle || meta) && (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ ml: onBack ? 4.5 : 0 }}>
              {subtitle}
            </Typography>
          ) : (
            <span />
          )}
          {meta && (
            <Typography variant="caption" color="text.secondary">
              {meta}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default DetailFormHeader;
